import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Hairline, Icon, Swatch, type SwatchKind } from "@/components/primitives";
import { MobileFrame } from "@/components/MobileFrame";
import { mergedItem, scanStore } from "@/lib/scanStore";
import { supabase } from "@/integrations/supabase/client";

interface CompRow {
  id: string;
  title: string;
  brand: string | null;
  category: string | null;
  era: string | null;
  price: number;
  ageDays: number;
  img: SwatchKind;
  match: number; // 0..100
}

const VALID_SWATCHES: SwatchKind[] = ["olive", "rust", "ink", "stripes", "cream"];
const swatchOf = (raw: string | null): SwatchKind =>
  (raw && (VALID_SWATCHES as string[]).includes(raw) ? raw : "olive") as SwatchKind;

// Match score: brand match is the strongest signal, category next, era a tiebreaker.
function scoreMatch(
  row: { brand: string | null; category: string | null; era: string | null },
  ref: { brand: string; category: string; era: string },
): number {
  let s = 50;
  const sameBrand = ref.brand && row.brand && row.brand.toLowerCase() === ref.brand.toLowerCase();
  const sameCat = ref.category && row.category && row.category.toLowerCase() === ref.category.toLowerCase();
  const sameEra = ref.era && row.era && row.era.toLowerCase() === ref.era.toLowerCase();
  if (sameBrand) s += 35;
  if (sameCat) s += 10;
  if (sameEra) s += 5;
  return Math.min(99, s);
}

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? Math.round((sorted[mid - 1] + sorted[mid]) / 2) : sorted[mid];
}

export default function Comps() {
  const nav = useNavigate();
  const item = useMemo(() => mergedItem(scanStore.getResult()), []);
  const [ticks, setTicks] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setTicks((v) => v + 1), 1800);
    return () => clearInterval(t);
  }, []);

  const brandKey = (item.brand ?? "").toLowerCase().trim();
  const categoryKey = (item.category ?? "").toLowerCase().trim();
  const eraKey = (item.era ?? "").toLowerCase().trim();
  const currentId = sessionStorage.getItem("fig_current_scan_id") ?? "";

  // Cached per brand+category. Stays fresh for 5 min, kept in cache 30 min.
  const { data: comps = null } = useQuery<CompRow[]>({
    queryKey: ["comps", brandKey, categoryKey, eraKey, currentId],
    enabled: brandKey.length > 0 || categoryKey.length > 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    queryFn: async () => {
      const filters: string[] = [];
      if (item.brand) filters.push(`brand.ilike.${item.brand}`);
      if (item.category) filters.push(`category.ilike.${item.category}`);
      if (filters.length === 0) return [];

      let q = supabase
        .from("scans")
        .select("id, title, brand, category, era, price_suggested, img, created_at")
        .eq("posted", true)
        .not("price_suggested", "is", null)
        .or(filters.join(","))
        .order("created_at", { ascending: false })
        .limit(60);

      if (currentId) q = q.neq("id", currentId);

      const { data, error } = await q;
      if (error || !data) return [];

      const now = Date.now();
      return data
        .filter((r) => Number.isFinite(Number(r.price_suggested)))
        .map((r) => ({
          id: r.id,
          title: r.title,
          brand: r.brand,
          category: r.category,
          era: r.era,
          price: Number(r.price_suggested),
          ageDays: (now - new Date(r.created_at).getTime()) / (24 * 60 * 60 * 1000),
          img: swatchOf(r.img),
          match: scoreMatch(r, { brand: item.brand, category: item.category, era: item.era }),
        }))
        .sort((a, b) => b.match - a.match || a.ageDays - b.ageDays);
    },
  });

  // Filter chips: All + each unique brand + each unique category from results.
  const filters = useMemo(() => {
    const set = new Set<string>(["All"]);
    if (item.brand) set.add(item.brand);
    if (item.category) set.add(item.category);
    (comps ?? []).forEach((c) => {
      if (c.brand) set.add(c.brand);
      if (c.category) set.add(c.category);
    });
    return Array.from(set).slice(0, 6);
  }, [comps, item.brand, item.category]);

  const [filter, setFilter] = useState("All");
  const shown = (comps ?? []).filter((c) => {
    if (filter === "All") return true;
    return c.brand === filter || c.category === filter;
  });

  // Stats panel — derived from the unfiltered result set so numbers stay stable.
  const stats = useMemo(() => {
    const list = comps ?? [];
    const exact = list.filter((c) => c.match >= 85).length;
    const med = median(list.map((c) => c.price));
    return {
      exact: list.length === 0 ? "—" : String(exact),
      median: med === 0 ? "—" : `$${med}`,
      sample: list.length,
    };
  }, [comps]);

  return (
    <MobileFrame>
      <div className="paper-bg" style={{ minHeight: "100vh", paddingBottom: 40 }}>
        <div style={{ padding: "12px 20px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button onClick={() => nav(-1)} style={{ background: "none", border: "none", padding: 6, cursor: "pointer", color: "hsl(var(--ink))" }}>{Icon.chev(18, "l")}</button>
          <span className="caps">Market Desk</span>
          <div style={{ width: 30 }} />
        </div>

        <div style={{ padding: "10px 20px 0" }}>
          <span className="caps" style={{ color: "hsl(var(--rust))" }}>Live comps · {item.title}</span>
          <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 900, fontSize: 44, color: "hsl(var(--ink))", letterSpacing: "-0.03em", lineHeight: 0.95, marginTop: 4 }}>
            The Market Desk
          </div>
        </div>

        <div style={{ padding: "12px 20px 0" }}>
          <div style={{ background: "hsl(var(--ink))", color: "hsl(var(--ink-fg))", padding: "10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 9, height: 9, borderRadius: "50%", background: "hsl(var(--rust-soft))", animation: "fig-pulse 1.2s infinite" }} />
            <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", flex: 1 }}>
              {comps === null
                ? <>Searching the archive…</>
                : comps.length === 0
                  ? <>No comparable posted scans yet</>
                  : <><span style={{ color: "hsl(var(--marigold))" }}>{comps.length + ticks}</span> comps in this category</>}
            </div>
            <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, color: "hsl(var(--sand))", letterSpacing: "0.1em" }}>·LIVE</span>
          </div>
        </div>

        <div style={{ padding: "14px 20px 0" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", border: "1px solid hsl(var(--ink))", background: "hsl(var(--cream))" }}>
            {[
              { v: stats.exact, l: "close matches" },
              { v: stats.median, l: "median price" },
              { v: stats.sample === 0 ? "—" : `${stats.sample}`, l: "in sample" },
            ].map((s, i) => (
              <div key={i} style={{
                padding: "10px 6px", textAlign: "center",
                borderRight: i < 2 ? "1px solid hsl(var(--ink))" : "none",
              }}>
                <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 18, color: "hsl(var(--ink))", lineHeight: 1 }}>{s.v}</div>
                <div className="caps" style={{ marginTop: 3, fontSize: 9 }}>{s.l}</div>
              </div>
            ))}
          </div>
        </div>

        {filters.length > 1 && (
          <div className="no-scrollbar" style={{ padding: "16px 20px 0", display: "flex", gap: 5, overflowX: "auto" }}>
            {filters.map((f) => (
              <button key={f} onClick={() => setFilter(f)} style={{
                padding: "5px 9px", border: "1px solid hsl(var(--ink))", cursor: "pointer", flexShrink: 0,
                background: filter === f ? "hsl(var(--ink))" : "transparent",
                color: filter === f ? "hsl(var(--cream))" : "hsl(var(--ink))",
                fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase",
              }}>{f}</button>
            ))}
          </div>
        )}

        <div style={{ padding: "12px 20px 0", display: "flex", flexDirection: "column", gap: 8 }}>
          {comps === null && (
            <div style={{
              border: "1px dashed hsl(var(--sand-deep))", padding: 18, textAlign: "center",
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: "hsl(var(--ink-2))",
            }}>
              Loading comps…
            </div>
          )}
          {comps !== null && shown.length === 0 && (
            <div style={{
              border: "1px dashed hsl(var(--sand-deep))", padding: 18, textAlign: "center",
              fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 14, color: "hsl(var(--ink-2))",
              lineHeight: 1.4,
            }}>
              No posted scans match {item.brand ? <em>{item.brand}</em> : "this brand"} or {item.category ? <em>{item.category}</em> : "this category"} yet. Be the first to post one.
            </div>
          )}
          {shown.map((c) => (
            <div key={c.id} style={{
              display: "flex", gap: 12, background: "hsl(var(--cream))",
              border: "1px solid hsl(var(--ink))", padding: 10, alignItems: "center",
            }}>
              <Swatch kind={c.img} style={{ width: 54, height: 54, border: "1px solid hsl(var(--ink))", flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <span className="caps" style={{ color: "hsl(var(--rust))" }}>{c.brand ?? c.category ?? "Vintage"}</span>
                  <span className="caps">{c.ageDays < 1 ? `${Math.max(1, Math.round(c.ageDays * 24))}h` : `${Math.round(c.ageDays)}d`} ago</span>
                </div>
                <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 14, color: "hsl(var(--ink))", lineHeight: 1.2, marginTop: 2 }}>
                  {c.title}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 5, alignItems: "baseline" }}>
                  <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: "hsl(var(--ink-2))" }}>match {c.match}%</span>
                  <span style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 18, color: "hsl(var(--ink))" }}>${c.price}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ padding: "16px 20px 0" }}>
          <Hairline dashed />
          <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: "hsl(var(--ink-2))", marginTop: 8, lineHeight: 1.5, textAlign: "center" }}>
            Comps drawn from posted scans across the Fig community.
          </div>
        </div>
      </div>
    </MobileFrame>
  );
}
